/** 个人信息页：角色字段、年级/班级展示、保存 payload 等纯逻辑 */

export const ROLE_LABEL_MAP = {
  student: '学生',
  teacher: '参训教师',
  normal: '师范生',
  assistant: '助教',
  expert: '专家',
  manager: '管理员',
  'bc-manager': '后台管理员',
  leader: '管理者',
};

/** 学段：本期后端不落库/不回显，仅前端展示用硬编码 */
export const STAGE_OPTIONS = [
  { id: 1, name: '幼儿园' },
  { id: 2, name: '小学' },
  { id: 3, name: '初中' },
  { id: 4, name: '高中' },
];

export const STAGE_ID_BY_NAME = STAGE_OPTIONS.reduce((acc, item) => {
  acc[item.name] = item.id;
  return acc;
}, {});

const STUDENT_FIELDS = [
  'name',
  'studyNumber',
  'sex',
  'phone',
  'area',
  'school',
  'stage',
  'clazz',
  'grade',
];

const TEACHER_FIELDS = [
  'name',
  'idCard',
  'sex',
  'phone',
  'email',
  'area',
  'school',
  'stage',
  'clazz',
  'subject',
  'title',
  'experience',
];

const STAFF_FIELDS = [
  'name',
  'idCard',
  'sex',
  'phone',
  'email',
  'area',
  'school',
  'subject',
  'title',
  'experience',
];

const NORMAL_FIELDS = [
  'name',
  'studyNumber',
  'sex',
  'phone',
  'area',
  'school',
  'grade',
  'clazz',
  'departmentName',
  'majorName',
  'birthYear',
  'sourceProvince',
  'sourceTypeName',
  'educationName',
  'nationName',
];

/** 字段展示顺序（并集去重后按此排） */
const FIELD_ORDER = [
  'name',
  'studyNumber',
  'idCard',
  'sex',
  'phone',
  'email',
  'area',
  'school',
  'stage',
  'clazz',
  'grade',
  'subject',
  'title',
  'experience',
  'departmentName',
  'majorName',
  'birthYear',
  'sourceProvince',
  'sourceTypeName',
  'educationName',
  'nationName',
];

export const FIELD_META = {
  name: { label: '姓名', prop: 'name' },
  studyNumber: { label: '学籍号/学号', prop: 'studyNumber' },
  idCard: { label: '身份证号', prop: 'idCard' },
  sex: { label: '性别', prop: 'sex' },
  phone: { label: '手机号', prop: 'phone' },
  email: { label: '邮箱', prop: 'email' },
  area: { label: '地区', prop: 'areaPath' },
  school: { label: '学校', prop: 'schoolId' },
  stage: { label: '学段', prop: 'stageName' },
  clazz: { label: '班级', prop: 'classId' },
  grade: { label: '年级', prop: 'enterYear' },
  subject: { label: '学科', prop: 'subjectId' },
  title: { label: '职称', prop: 'titleId' },
  experience: { label: '任教教龄', prop: 'experience' },
  departmentName: { label: '院系', prop: 'departmentName', pendingDict: true },
  majorName: { label: '专业', prop: 'majorName', pendingDict: true },
  birthYear: { label: '出生年份', prop: 'birthYear', pendingDict: true },
  sourceProvince: { label: '生源地', prop: 'sourceProvince', pendingDict: true },
  sourceTypeName: { label: '生源类别', prop: 'sourceTypeName', pendingDict: true },
  educationName: { label: '学历', prop: 'educationName', pendingDict: true },
  nationName: { label: '民族', prop: 'nationName', pendingDict: true },
};

const TEACHER_LIKE = new Set(['teacher']);
const STAFF_LIKE = new Set(['assistant', 'expert', 'manager', 'bc-manager', 'leader']);

export const normalizeUserRoles = (userRoleList = [], fallbackRole = '') => {
  const values = (Array.isArray(userRoleList) ? userRoleList : [])
    .map((item) => (typeof item === 'string' ? item : item && item.value))
    .filter(Boolean);
  const uniq = [...new Set(values)];
  if (!uniq.length && fallbackRole) uniq.push(fallbackRole);
  return uniq;
};

export const getRoleLabels = (roles = []) =>
  roles.map((value) => ROLE_LABEL_MAP[value] || value);

export const shouldShowIntroduce = (roles = []) =>
  roles.some((r) => TEACHER_LIKE.has(r) || STAFF_LIKE.has(r));

export const isNormalStudentGrade = (roles = []) => roles.includes('normal');

export const getVisibleFieldsForRoles = (roles = []) => {
  const set = new Set();
  roles.forEach((role) => {
    if (role === 'student') STUDENT_FIELDS.forEach((f) => set.add(f));
    else if (role === 'teacher') TEACHER_FIELDS.forEach((f) => set.add(f));
    else if (role === 'normal') NORMAL_FIELDS.forEach((f) => set.add(f));
    else if (STAFF_LIKE.has(role)) STAFF_FIELDS.forEach((f) => set.add(f));
  });
  // 学籍号文案：纯师范生用「学号」，含学生用「学籍号」，并集用「学籍号/学号」
  const fields = FIELD_ORDER.filter((key) => set.has(key)).map((key) => {
    const meta = { ...FIELD_META[key], key };
    if (key === 'studyNumber') {
      const hasStudent = roles.includes('student');
      const hasNormal = roles.includes('normal');
      if (hasStudent && !hasNormal) meta.label = '学籍号';
      else if (!hasStudent && hasNormal) meta.label = '学号';
      else meta.label = '学籍号/学号';
    }
    return meta;
  });
  return fields;
};

const K12_GRADE_NAMES = [
  '一年级',
  '二年级',
  '三年级',
  '四年级',
  '五年级',
  '六年级',
  '七年级',
  '八年级',
  '九年级',
  '十年级',
  '十一年级',
  '十二年级',
];

const NORMAL_GRADE_NAMES = ['大一', '大二', '大三', '大四', '大五'];

export const calcGradeIndex = (enterYear, maxGrade = 12) => {
  const year = Number(enterYear);
  if (!year) return null;
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  let grade = currentYear - year + (currentMonth >= 9 ? 1 : 0);
  if (grade < 1) grade = 1;
  if (grade > maxGrade) grade = maxGrade;
  return grade;
};

export const formatGradeWithEnterYear = (enterYear, gradeName, roles = []) => {
  if (gradeName && !enterYear) return gradeName;
  const isNormal = isNormalStudentGrade(roles);
  const max = isNormal ? 5 : 12;
  const names = isNormal ? NORMAL_GRADE_NAMES : K12_GRADE_NAMES;
  const idx = calcGradeIndex(enterYear, max);
  if (idx == null) return gradeName || '';
  const name = names[idx - 1] || gradeName || '';
  return enterYear ? `${name}（${enterYear}）` : name;
};

const extractClassNo = (className = '') => {
  const text = String(className || '');
  const match = text.match(/(\d+)\s*班/);
  if (match) return match[1];
  const num = text.match(/(\d+)/);
  return num ? num[1] : text.replace(/班/g, '') || '';
};

/**
 * 班级展示：有独立年级字段且有值 → 只显示班级名；
 * 否则 → 「三年级（1）班」
 */
export const formatClassDisplay = ({
  className,
  enterYear,
  formEnterYear,
  hasGradeField,
  gradeValue,
  roles = [],
} = {}) => {
  const name = className || '';
  if (!name && !enterYear && !formEnterYear) return '';
  if (hasGradeField && (gradeValue || formEnterYear || enterYear)) {
    return name || '无';
  }
  const year = enterYear || formEnterYear;
  const isNormal = isNormalStudentGrade(roles);
  const max = isNormal ? 5 : 12;
  const names = isNormal ? NORMAL_GRADE_NAMES : K12_GRADE_NAMES;
  const idx = calcGradeIndex(year, max);
  const gradeText = idx != null ? names[idx - 1] : '';
  const classNo = extractClassNo(name);
  if (gradeText && classNo) return `${gradeText}（${classNo}）班`;
  if (gradeText) return `${gradeText}班`;
  return name || '';
};

export const formatAreaDisplay = (form = {}) => {
  if (form.areaName) return form.areaName;
  if (form.area && typeof form.area === 'string') return form.area;
  const info = form.areaInfo;
  if (info) {
    const parts = [info.province, info.city, info.area]
      .map((item) => (item && (item.name || item.label)) || '')
      .filter(Boolean);
    if (parts.length) return parts.join('/');
  }
  return '';
};

export const parseAreaPathFromForm = (form = {}) => {
  const info = form.areaInfo;
  if (info) {
    const ids = [info.province, info.city, info.area]
      .map((item) => (item == null ? null : item.id ?? item.value ?? item))
      .filter((v) => v != null && v !== '');
    if (ids.length) return ids;
  }
  if (Array.isArray(form.areaPath) && form.areaPath.length) return [...form.areaPath];
  if (form.areaId != null && form.areaId !== '') return [form.areaId];
  return [];
};

export const displayOrEmpty = (val) => {
  if (val === 0 || val === '0') return String(val);
  if (val == null || val === '') return '无';
  return String(val);
};

export const getChangedIdentityFields = (current = {}, snapshot = {}) => {
  const changed = {};
  const curName = (current.name || '').trim();
  const snapName = (snapshot.name || '').trim();
  const curPhone = String(current.phone == null ? '' : current.phone).trim();
  const snapPhone = String(snapshot.phone == null ? '' : snapshot.phone).trim();
  if (curName !== snapName) changed.name = curName;
  if (curPhone !== snapPhone) changed.phone = curPhone;
  return changed;
};

const pickFirst = (obj, keys) => {
  for (let i = 0; i < keys.length; i++) {
    const k = keys[i];
    if (obj[k] != null && obj[k] !== '') return obj[k];
  }
  return undefined;
};

export const normalizeProfileForm = (raw = {}) => {
  // 后端回显学号为 studentCode
  const studyNumber = pickFirst(raw, ['studentCode', 'studyNumber']);
  const classId = pickFirst(raw, ['classId', 'clazzId']);
  const className = pickFirst(raw, ['clazzName', 'className']);
  const enterYear = pickFirst(raw, ['enterYear', 'enrollYear', 'enrollmentYear']);
  const areaName = formatAreaDisplay(raw);
  const areaPath = parseAreaPathFromForm(raw);
  return {
    ...raw,
    studyNumber: studyNumber != null ? studyNumber : raw.studyNumber,
    studentCode: studyNumber != null ? studyNumber : raw.studentCode,
    classId: classId != null ? classId : raw.classId,
    clazzName: className != null ? className : raw.clazzName,
    className: className != null ? className : raw.className,
    enterYear: enterYear != null ? enterYear : raw.enterYear,
    areaName,
    area: areaName || raw.area,
    areaPath,
    province: areaPath[0] != null ? areaPath[0] : raw.province,
    city: areaPath[1] != null ? areaPath[1] : raw.city,
    sex: raw.sex === 0 || raw.sex === '0' ? 0 : raw.sex === 1 || raw.sex === '1' ? 1 : raw.sex,
  };
};

/**
 * 白名单组装 update_info payload（对齐后端对接文档）
 * - 不传 stage / 师范生扩展（本期不支持）
 * - 学号字段：studentCode
 * - 班级主键：classId
 */
export const buildFixInfoPayload = (form = {}, roles = []) => {
  const payload = {};

  const setIfHas = (key, val) => {
    if (val != null && val !== '') payload[key] = val;
  };

  setIfHas('name', form.name);
  setIfHas('avatar', form.avatar);
  if (form.sex === 0 || form.sex === '0' || form.sex === 1 || form.sex === '1') {
    payload.sex = Number(form.sex);
  }
  if (form.phone != null && form.phone !== '') {
    const phoneNum = Number(form.phone);
    payload.phone = Number.isNaN(phoneNum) ? form.phone : phoneNum;
  }

  setIfHas('email', form.email);
  setIfHas('idCard', form.idCard);
  setIfHas('introduce', form.introduce);
  if (form.experience != null && form.experience !== '') {
    payload.experience = form.experience;
  }

  const path = Array.isArray(form.areaPath) ? form.areaPath : [];
  const province = path[0] != null ? path[0] : form.province;
  const city = path[1] != null ? path[1] : form.city;
  const area = path[2] != null ? path[2] : form.areaId;
  setIfHas('province', province);
  setIfHas('city', city);
  setIfHas('area', area);
  const areaId = form.areaId != null && form.areaId !== '' ? form.areaId : area;
  setIfHas('areaId', areaId);

  setIfHas('schoolId', form.schoolId);

  const classId = form.classId != null && form.classId !== '' ? form.classId : form.clazzId;
  setIfHas('classId', classId);

  const hasStudentOrNormal = roles.some((r) => r === 'student' || r === 'normal');
  if (hasStudentOrNormal) {
    const code =
      form.studentCode != null && form.studentCode !== ''
        ? form.studentCode
        : form.studyNumber;
    setIfHas('studentCode', code);
  }

  const hasTeacherOrStaff = roles.some(
    (r) => TEACHER_LIKE.has(r) || STAFF_LIKE.has(r)
  );
  if (hasTeacherOrStaff) {
    setIfHas('subjectId', form.subjectId);
    setIfHas('titleId', form.titleId);
  }

  return payload;
};

export const isUserExistResponse = (resData = {}) => {
  // check_exist: { code, data: { exists, exist } }
  const nested = resData && resData.data;
  if (nested && typeof nested === 'object') {
    if (nested.exists === true || nested.exist === true) return true;
  }
  if (resData && (resData.exists === true || resData.exist === true)) return true;
  if (resData && resData.code === 409) return true;
  if (nested && nested.code === 409) return true;
  const msg = (resData && resData.msg) || (nested && nested.msg) || '';
  return typeof msg === 'string' && msg.indexOf('已存在') > -1;
};
